<template>
  <div @input="scheduleVolatileWidths" @focusout="scheduleVolatileWidths">
    <OCRScanDialog :formProperty="ocrScanFormProperty" :showDlg="ocrDialogVisible" @dlgClose="ocrScanDialogClose" @ocrDone="ocrDone"/>

    <template v-if="!volatile">
      <div class="row">
        <component
          class="col-sm-6 col-xs-12 q-py-sm q-px-sm-sm"
          ref="cmpFrmField"
          v-for="paramField in paramFields.filter(p => !p.systemParameter)"
          :is="paramField.component"
          :key="paramField.id" :formProperty="paramField" :volatile="volatile"
          :queryID="queryID" @onChange="onFieldValueChange" @useOCRScanDialog="useOCRScanDialog"
        />
      </div>
      <br/>
      <div class="row justify-end q-pa-sm">
        <q-btn size="md" class="float-left " color="blue-grey-3" unelevated @click="btnSetDefaultValues()" label="Standard">
          <q-tooltip class="secondary">{{ $t('useFormularStandardValues') }}</q-tooltip>
        </q-btn>
        <q-btn size="md" class="float-right q-ml-xs-sm q-ml-sm-lg" color="primary" unelevated @click="btnOK(false)" data-test="callParamQuery">{{ $t('execute') }}</q-btn>
      </div>
    </template>

    <template v-else>
      <!-- components with "pin_" prefix are always visible -->
      <div class="row" v-if="pinnedVolatileParams.length > 0">
        <div
          class="volatile-param-field q-pt-sm q-pr-sm"
          v-for="paramField in pinnedVolatileParams"
          :key="paramField.id" :data-param-name="paramField.nativename"
          :style="{width: paramField.compWidth + 'px'}"
        >
          <component
            ref="cmpFrmField"
            :is="paramField.component"
            :formProperty="paramField" :volatile="volatile"
            :queryID="queryID" @onChange="onFieldValueChange" @onEnter="btnOK" @useOCRScanDialog="useOCRScanDialog"
          />
        </div>
      </div>
      <div class="row" v-if="(!$q.platform.is.mobile || paramFields.filter(p => !p.systemParameter && p.paramVolatile).length<2) && (!appEmbedded || paramFields.filter(p => !p.systemParameter && p.paramVolatile).length<=4)">
        <div
          class="volatile-param-field q-pt-sm q-pr-sm"
          v-for="paramField in unpinnedVolatileParams"
          :key="paramField.id" :data-param-name="paramField.nativename"
          :style="{width: paramField.compWidth + 'px'}"
        >
          <component
            ref="cmpFrmField"
            :is="paramField.component"
            :formProperty="paramField" :volatile="volatile"
            :queryID="queryID" @onChange="onFieldValueChange" @onEnter="btnOK" @useOCRScanDialog="useOCRScanDialog"
          />
        </div>
        <q-btn v-if="getVisibleVolatileParamCount()>=1" class="float-right q-mr-none q-mt-sm" style="height:40px;width:50px" outline dense color="primary"
               unelevated @click="btnOK(false)" data-test="callParamQuery" icon="check"></q-btn>
      </div>
      <div v-else>
        <!-- mobile volatile parameter -->
        <q-btn flat dense size="md" icon="filter_alt" class="q-pl-none q-mb-xs" v-if="unpinnedVolatileParams.length > 0"
               @click="showMobileVolativeParams=true" label="Filter" :color="isFilterActive()?'negative':'primary'"/>

        <div class="q-gutter-xs row" style="max-width: 300px">
          <q-chip v-for="paramField in unpinnedVolatileParams"
                  removable dense
                  color="primary"
                  v-model="paramField.filterchip"
                  text-color="white"
                  @remove="paramField.value = paramField.defaultValue; paramField.filterchip = false; btnOK(false)"
                  :label="getChipLabel(paramField)"
          />
        </div>
      </div>
      <q-dialog v-model="showMobileVolativeParams" position="top" @hide="btnOK(false)"> <!-- persistent="$q.platform.is.safari" -->
        <q-card>
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h6">{{ $t('filter') }}</div>
            <q-space/>
            <q-btn icon="close" flat round dense v-close-popup/>
          </q-card-section>
          <q-card-section class="row q-pb-none">
            <component
              class="col-12 q-pr-xs-sm"
              ref="cmpFrmField"
              v-for="paramField in unpinnedVolatileParams"
              :is="paramField.component"
              :key="paramField.id" :formProperty="paramField" :volatile="volatile"
              :queryID="queryID" @onChange="onFieldValueChange" @useOCRScanDialog="useOCRScanDialog"
            />
          </q-card-section>
          <q-card-actions align="right" class="q-pr-lg q-mb-lg">
            <q-btn color="primary" unelevated @click="showMobileVolativeParams=false" icon="check"></q-btn>
          </q-card-actions>
        </q-card>
      </q-dialog>
    </template>

  </div>
</template>

<script>
import FormProperty from '../classes/FormProperty'
import {StaticData} from '../classes/StaticData'
import {useAuthStore} from 'src/stores/AuthStore.js'
import {useQueryStore} from 'src/stores/QueryStore.js'
import OCRScanDialog from "components/forms/OCRScanDialog.vue";
import HistoryBack from "components/plugins/netzportal/HistoryBack.vue";

export default {
  name: 'parameter',
  props: {
    queryMeta: Object,
    queryParameter: {
      Array,
      default: () => []
    },
    volatile: {
      Boolean,
      default: false
    },
    appEmbedded: {
      Boolean,
      default: false
    },
    autoSkipParameter: {
      Boolean,
      default: false
    }
  },

  components: {OCRScanDialog},
  data() {
    return {
      StaticData,
      queryID: this.queryMeta.id ? this.queryMeta.id : this.$route.params.id,
      clearSelectedRow: this.$route.params.clearSelectedRow ? this.$route.params.clearSelectedRow : false,
      paramFields: [],  // comes from store - directly updated form components
      ignoredSystemParameter: ['ssiteid', 'ssessionid', 'suserloginname', 'surl', 'soutputmsg'],
      btnOKDisabled: false,
      onlySystemparameter: true,
      showMobileVolativeParams: false,
      ocrScanFormProperty: null,
      ocrDialogVisible: false
    }
  },

  computed: {
    pinnedVolatileParams() {
      return this.paramFields.filter(p => !p.systemParameter && p.paramVolatile && p.nativename.startsWith('pin_'))
    },
    unpinnedVolatileParams() {
      return this.paramFields.filter(p => !p.systemParameter && p.paramVolatile && !p.nativename.startsWith('pin_'))
    }
  },

  methods: {
    scheduleVolatileWidths() {
      if (!this.volatile || this._volatileWidthsPending) return
      this._volatileWidthsPending = true
      this.$nextTick(() => {
        this._volatileWidthsPending = false
        if (!this.$el || this._volatileWidthsDisposed) return
        this.updateVolatileWidths()
      })
    },

    updateVolatileWidths() {
      // Measure the rendered display value, not a value-list key or raw date.
      const context = this._volatileWidthContext || (this._volatileWidthContext = document.createElement('canvas').getContext('2d'))
      if (!context) return
      this.$el.querySelectorAll('.volatile-param-field').forEach((element) => {
        const field = this.paramFields.find(p => p.nativename === element.dataset.paramName)
        if (!field || !element.getClientRects().length) return
        const native = element.querySelector('.q-field__native, .q-checkbox__label')
        if (!native) return
        const style = window.getComputedStyle(native)
        context.font = style.font || `${style.fontSize} ${style.fontFamily}`
        // QSelect repeats its selected label in an invisible keyboard-focus input.
        // Count only displayed text and editable inputs, not that duplicate value.
        const inputValues = [...element.querySelectorAll('input:not([type="checkbox"]):not([type="hidden"]):not(.q-select__focus-target)')].map(input => input.value)
        const text = [native.textContent.trim(), ...inputValues].filter(Boolean).join(' ')
        const valueWidth = context.measureText(text).width
        const labelWidth = context.measureText(field.title || '').width
        const iconWidth = [...element.querySelectorAll('.q-field__append, .q-field__prepend')]
          .reduce((width, icon) => width + icon.getBoundingClientRect().width, 0)
        // Include field padding, toolbar spacing and room for a clear icon.
        let width = Math.ceil(Math.max(labelWidth, valueWidth) + iconWidth + 40)
        if (field.component === 'fldDateTime') {
          const types = StaticData.DATATYPES
          width = field.dataTypeID === types.TYPE_TIME ? 100 : field.dataTypeID === types.TYPE_DATE ? 160 : 220
        } else {
          width = Math.max(field.component === 'fldCheckbox' ? 48 : 100, width)
        }
        width = Math.min(300, width)
        // Do not move neighbouring controls back and forth while typing.
        if (element.contains(document.activeElement)) width = Math.max(field.compWidth, width)
        if (field.compWidth !== width) field.compWidth = width
      })
    },

    ocrDone(scanResult) {
      this.eventBus.refreshCompDefault(null, this.ocrScanFormProperty.nativename, scanResult, this.ocrScanFormProperty.defaultValue, '', '', undefined, this.ocrScanFormProperty.nativename, true)
      this.ocrScanDialogClose()
    },

    ocrScanDialogClose() {
      this.ocrDialogVisible = false
    },

    useOCRScanDialog(data) {
      console.info('[OCR] Scan-Anfrage wurde von Parameter.vue empfangen', {
        ocrType: data.formProperty?.ocrType,
        field: data.formProperty?.nativename
      })
      this.ocrScanFormProperty = data.formProperty
      this.ocrDialogVisible = true
      data.onDialogOpened?.()
    },

    btnSetDefaultValues() {
      this.paramFields.forEach((field) => {
        field.paramInfoHelp = new Date()  // hack!!!! cause only value update does not trigger the watcher
        field.value = field.defaultValue
        this.onFieldValueChange(field)
      })
    },

    btnOK(autoskip) {
      let paramsInvalid = false
      this.paramFields.forEach((field) => {
        if (field.validate) {
          field.validate()
          if (field.invalid) {
            paramsInvalid = true
          }
        }
      })

      //logger.info("paramsInvalid:", paramsInvalid)

      if (paramsInvalid) {
        this.$emit('parameterEntered', {invalid: paramsInvalid})

        this.$q.dialog({
          title: this.$t('wrongParamValues'),
          message: this.$t('wrongParamValuesDesc')
        })

      } else {
        // write fields in localstorage for the next time
        this.paramFields.forEach((field) => {
          if (field.paramMruFirst) {
            if (typeof field.value !== 'undefined' && field.value !== null) {
              localStorage.setItem(this.queryID + "__" + field.name, field.value)
            } else {
              if (!autoskip) {
                localStorage.removeItem(this.queryID + "__" + field.name)
                logger.info("!!! Remove item: ", this.queryID + "__" + field.name)
              }
            }
          } else {
            localStorage.removeItem(this.queryID + "__" + field.name)
          }
        })
        //logger.info("emit parameterEntered")
        this.$emit('parameterEntered', {paramFields: this.paramFields, clearSelectedRow: this.clearSelectedRow, onlySystemparameter: this.onlySystemparameter})
        this.showMobileVolativeParams = false
      }
    },


    /**
     *  onFieldValueChange
     *  ----------------------------------------------------------------------------------------------------
     *  Event triggered by @input
     *  Build a key/value list of all current parameter value in model
     *  Send to 'valueListParameterSQLChange' service
     **/
    onFieldValueChange(changedParam) {
      const queryStore = useQueryStore()
      this.eventBus.showCompSpinner(changedParam.id, true)

      // 1st: get all fields from model as paramID/value array with actual values
      let fieldKeyValueList = []
      for (let i = 0; i < this.paramFields.length; i++) {
        // build key/value parameterlist for valueListParameterSQLChange call. ignore fields after changed param
        let field = this.paramFields[i]
        let keyValue = {key: field.nativename, value: field.value ? field.value : field.defaultValue}
        fieldKeyValueList.push(keyValue)
      }

      // 2nd: send list of objClassID / value pairs to 'valueListSQLChange' service
      // logger.info("onFieldValueChange, queryID: ", this.queryID)
      // logger.info("onFieldValueChange, paramID:", changedParam.classID, changedParam)
      queryStore.valueListParameterSQLChange({fieldKeyValueList: fieldKeyValueList, paramID: changedParam.classID, queryID: this.queryID})
        .then((fieldKeyValueList) => {
          if (fieldKeyValueList.length > 0) {
            //if (changedParam.deleteDependentFields) {   // true when onChange event was fired in mounted hook
            this.clearValueListCompAfterChanged(changedParam, fieldKeyValueList)
            //}

            // get back dependent key/value list (key=nativename, value=csv of values) of changedFieldClassID
            for (let keyValueOfField of fieldKeyValueList) {
              this.eventBus.refreshCompDefault(null, keyValueOfField.key, keyValueOfField.value, keyValueOfField.defaultValue, keyValueOfField.errorMsg, keyValueOfField.errorMsg, undefined, changedParam.nativename, true)
            }
          }
          this.eventBus.showCompSpinner(changedParam.id, false)

          if (changedParam.submit) {  // submit is true if field component gets keydown.enter
            this.btnOK(false)
          }
        })
        .catch(error => {
          console.error("Fehler beim Aktualisieren abhängiger Formularfelder.", error)
          // let errMsg = "Fehler beim Aktualisieren abhängiger Formularfelder."
          // this.eventBus.showCompSpinner(changedParam.id, false)
          // this.eventBus.globalErrorHandling(errMsg, error)
        })
    },

    /**
     *  clearValueListCompAfterChanged
     *  Search for changed field and set all following valuelists to default value
     **/
    clearValueListCompAfterChanged(changedParam, fieldKeyValueList) {
      // clear all valueList fields with sql stmt, except the changedField
      //console.warn("clearValueListCompAfterChanged", changedParamNativeName, fieldKeyValueList)

      fieldKeyValueList.forEach((field) => {
        //console.error("clear field: ", field.key, "!=", changedParamNativeName)
        if (field.key !== changedParam.nativename) {
          //logger.info("clear field with name: ", field.key)
          this.eventBus.clearValueListComp(field.key, "", changedParam.nativename)
        }
      })
    },

    getVisibleVolatileParamCount() {
      return this.paramFields.filter((paramField) => {
        // console.log("paramField.instantRefresh", paramField.title, '=',  paramField.instantRefresh)
        if (paramField.paramVolatile && (!paramField.instantRefresh && paramField.instantRefresh !== undefined) && this.ignoredSystemParameter.indexOf(paramField.name.toLowerCase()) === -1) return true
        else return false
      }).length
    },

    isFilterActive() {
      let changed = false
      this.paramFields.forEach((field) => {
        if (field.value && (field.value != field.defaultValue)) {
          changed = true
          field.filterchip = true
        } else {
          field.filterchip = false
        }
      })
      return changed
    },

    getChipLabel(paramField) {
      return paramField.title + ':' + (paramField.valueLabel ? paramField.valueLabel : paramField.value)
    },

    autoSkipParams() {
      const authStore = useAuthStore()
      let queryParamAutoSubmit = authStore.queryParamListFromDeepLink["sbm"]

      //logger.info("-- Autoskip Parameter...",this.queryMeta,this.queryMeta.skipParamsOnDefault,  this.onlySystemParameter, queryParamAutoSubmit, this.queryParameter.autoSkipParamsDone)
      //logger.info("-- onlySystemParameter...", this.onlySystemParameter)
      //logger.info("-- queryParamAutoSubmit...", queryParamAutoSubmit)
      //logger.info("-- autoSkipParamsDone...", this.queryParameter.autoSkipParamsDone)

      if ((this.queryMeta.skipParamsOnDefault || this.onlySystemparameter || queryParamAutoSubmit !== undefined || this.autoSkipParameter) && !this.queryParameter.autoSkipParamsDone) { //
        this.queryParameter.autoSkipParamsDone = true  // set here only for check in above if - so no endless loop happens
        history.state.autoSkipParameter = false
        //console.error("Parameter: skikParamsOnDefault or only sysParams: submit parameter")
        //setTimeout(() => this.btnOK(), 500)
        this.btnOK(true)
      }
    },

    init() {
      let tmpValueList = []
      this.onlySystemparameter = true
      this.paramFields = []

      for (let i = 0; i < this.queryParameter.length; i++) {
        let formProperty = FormProperty.createFromSQLDeclare(this.queryParameter[i])

        // mark property as systemParamter
        if (this.ignoredSystemParameter.indexOf(formProperty.name.toLowerCase()) !== -1) {
          formProperty.systemParameter = true
          //logger.info("param: ", formProperty.name, "is system parameter")
        } else {
          this.onlySystemparameter = false
          //logger.info("found at least one non system parameter")
        }

        switch (formProperty.dataTypeID) {
          case StaticData.DATATYPES.TYPE_INT:
          case StaticData.DATATYPES.TYPE_LONG:
          case StaticData.DATATYPES.TYPE_INT64:
          case StaticData.DATATYPES.TYPE_DOUBLE:
            formProperty.component = "fldNumber";
            break;

          case StaticData.DATATYPES.TYPE_FLOAT:
            formProperty.component = "fldFloat";
            //item.component = fldOCR;
            break;

          case StaticData.DATATYPES.TYPE_STRING:
          case StaticData.DATATYPES.TYPE_MEMO:
            formProperty.component = "fldString";
            break;

          case StaticData.DATATYPES.TYPE_BOOLEAN:
            formProperty.component = "fldCheckbox";
            break;

          case StaticData.DATATYPES.TYPE_DATE:
          case StaticData.DATATYPES.TYPE_TIME:
          case StaticData.DATATYPES.TYPE_DATETIME:
            formProperty.component = "fldDateTime";
            break;

          case StaticData.DATATYPES.TYPE_VALUELIST:
            formProperty.component = "fldValueList";
            tmpValueList.push(formProperty)
            break;

          case StaticData.DATATYPES.TYPE_AUTOCOMPLETE:
            formProperty.component = "fldAutocomplete";
            break;
        }

        //formProperty.value = formProperty.defaultValue   // add value field die param definitions from portal server
        formProperty.readonly = false
        formProperty.autofocus = (i === 0) ? true : false
        formProperty.invalid = false
        formProperty.filterchip = false
        formProperty.compWidth = 130
        formProperty.isParameter = true

        //logger.info("formProperty=", formProperty.title, formProperty.defaultValue)
        this.paramFields.push(formProperty)
      }

      //logger.info("paramFields=", this.paramFields)
      //logger.info("Parameter:", this.queryMeta)
    },

    refreshDetailView() {
      if (!this.volatile) {
        this.queryParameter.autoSkipParamsDone = false
        this.autoSkipParams()
      }
    }
  },

  beforeUnmount: function () {
    this._volatileWidthsDisposed = true
    this._volatileWidthObserver?.disconnect()
    //this.eventBus.off('refreshDetailView', this.refreshDetailView)
  },

  updated() {
    this.scheduleVolatileWidths()
  },

  mounted: function () {
    this.init()
    if (this.volatile) {
      // Value-list labels can change inside a child without updating this parent.
      this._volatileWidthObserver = new MutationObserver(() => this.scheduleVolatileWidths())
      this._volatileWidthObserver.observe(this.$el, {subtree: true, childList: true, characterData: true})
    }
    this.scheduleVolatileWidths()
    //logger.info("Parameter Mount Done for " + this.queryMeta.title + ", volatile:", this.volatile, ", queryID:", this.queryID, ", queryParameter:", this.queryParameter)
    if (!this.volatile || this.autoSkipParameter) {
      this.$emit('refreshFavList')
      //this.eventBus.on('refreshDetailView', this.refreshDetailView)
      this.autoSkipParams()
    }

  }
}
</script>

<style lang="sass">
.volatile-param-field
  flex: 0 1 auto
  min-width: 0
  max-width: 100%
  > div
    width: 100%
  .q-field__native > div:not(.q-chip)
    max-width: 100%
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.paramH3
  font-size: 2em
  line-height: 1em
  margin: 0.5em 7px 25px 7px
</style>
